/**
 * conversationRouteSync — pure helpers for ConversationRouteSync's URL→State
 * sync (see app/App.tsx).
 *
 * When the URL carries a `:conversationId` that is NOT present in the loaded
 * conversation list, ConversationRouteSync's default behaviour is to redirect
 * to the first available conversation — the recovery path for a genuinely
 * stale/deleted real id.
 *
 * That recovery must NOT fire for the OPTIMISTIC temporary id the new-chat flow
 * stamps into the URL (`conversation-<timestamp>`): that id is intentionally
 * absent from the server list until `createConversation`'s onSuccess swaps in
 * the real id and navigates (ConversationContext.tsx). Redirecting on it bounces
 * the user straight back to their previous conversation (#4385). The temp-id
 * guard mirrors the `!startsWith('conversation-')` pattern already used in
 * ConversationContext.
 *
 * Deliberately framework-free (no React, no router) so it is exhaustively
 * unit-testable.
 */

/** Prefix the optimistic new-chat flow stamps onto a not-yet-persisted id. */
export const TEMP_CONVERSATION_ID_PREFIX = 'conversation-';

/** True for the optimistic temp id created before the server assigns a real id. */
export function isTempConversationId(conversationId: string): boolean {
  return conversationId.startsWith(TEMP_CONVERSATION_ID_PREFIX);
}

/**
 * Decide whether ConversationRouteSync should redirect the URL's
 * `conversationId` to the first available conversation.
 *
 * Returns:
 *   - `false` for an optimistic temp id (`conversation-*`) — let onSuccess
 *     perform the real-id navigation; never bounce the new chat away.
 *   - `false` when the id IS present in the list — nothing to recover.
 *   - `false` when the list is empty — there is no first conversation to go to.
 *   - `true` only for a genuinely-missing REAL id with a non-empty list — the
 *     legitimate stale/deleted-id recovery path.
 */
export function shouldRedirectToFirstConversation(
  conversationId: string,
  list: ReadonlyArray<{ id: string }>,
): boolean {
  if (isTempConversationId(conversationId)) return false;
  if (list.length === 0) return false;
  if (list.some((item) => item.id === conversationId)) return false;
  return true;
}
